import { sessionIdKey } from './pairing.js';

export type RelaySignalPacket =
  | { kind: 'ready' }
  | { kind: 'phone-key-response'; payload: string }
  | { kind: 'pairing-approved'; payload: string }
  | { kind: 'sdp-offer'; payload: string }
  | { kind: 'sdp-answer'; payload: string }
  | { kind: 'ice-candidate'; payload: string };

export type BrowserSignalPacket =
  { kind: 'sdp-offer'; payload: string } | { kind: 'ice-candidate'; payload: string };

type SocketLike = {
  readyState: number;
  send(payload: string): void;
  close(): void;
  onopen: ((event?: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
  onerror: ((event?: unknown) => void) | null;
  onclose: ((event?: unknown) => void) | null;
};

export type BrowserSignalingClientOptions = {
  signalingOrigin: string;
  sessionId: Uint8Array;
  socketFactory?: (url: string) => SocketLike;
  onPacket?: (packet: RelaySignalPacket) => void;
  onClose?: () => void;
};

/** Browser-side WSS boundary; it never stores or interprets application payloads. */
export class BrowserSignalingClient {
  private readonly options: BrowserSignalingClientOptions;
  private socket: SocketLike | null = null;

  constructor(options: BrowserSignalingClientOptions) {
    const origin = new URL(options.signalingOrigin);
    if (
      (origin.protocol !== 'wss:' &&
        !(origin.protocol === 'ws:' && isLoopbackHostname(origin.hostname))) ||
      origin.username ||
      origin.password ||
      origin.pathname !== '/'
    ) {
      throw new Error('INVALID_SIGNALING_ORIGIN');
    }
    if (options.sessionId.byteLength !== 16) throw new Error('INVALID_SESSION_ID');
    this.options = { ...options, signalingOrigin: origin.origin };
  }

  connect(): Promise<void> {
    if (this.socket) throw new Error('SIGNAL_ALREADY_CONNECTED');
    const factory =
      this.options.socketFactory ?? ((url: string) => new WebSocket(url) as unknown as SocketLike);
    const socket = factory(
      `${this.options.signalingOrigin}/signal?session=${sessionIdKey(this.options.sessionId)}&role=browser`,
    );
    this.socket = socket;
    return new Promise<void>((resolve, reject) => {
      socket.onopen = () => resolve();
      socket.onerror = () => reject(new Error('SIGNAL_CONNECTION_FAILED'));
      socket.onclose = () => this.options.onClose?.();
      socket.onmessage = (event: { data: unknown }) => {
        const packet = decodeRelaySignalPacket(event.data);
        this.options.onPacket?.(packet);
      };
    });
  }

  send(packet: BrowserSignalPacket): void {
    const socket = this.socket;
    if (!socket || socket.readyState !== 1) throw new Error('SIGNAL_NOT_CONNECTED');
    socket.send(JSON.stringify(packet));
  }

  close(): void {
    this.socket?.close();
    this.socket = null;
  }
}

function isLoopbackHostname(hostname: string): boolean {
  return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
}

export function decodeRelaySignalPacket(raw: unknown): RelaySignalPacket {
  if (typeof raw !== 'string' || raw.length > 48 * 1024) throw new Error('INVALID_SIGNAL_PACKET');
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    throw new Error('INVALID_SIGNAL_PACKET');
  }
  if (!value || typeof value !== 'object') throw new Error('INVALID_SIGNAL_PACKET');
  const body = value as Record<string, unknown>;
  if (body.kind === 'ready') return { kind: 'ready' };
  if (
    (body.kind !== 'phone-key-response' &&
      body.kind !== 'pairing-approved' &&
      body.kind !== 'sdp-offer' &&
      body.kind !== 'sdp-answer' &&
      body.kind !== 'ice-candidate') ||
    typeof body.payload !== 'string' ||
    body.payload.length < 1 ||
    body.payload.length > 32 * 1024
  ) {
    throw new Error('INVALID_SIGNAL_PACKET');
  }
  return { kind: body.kind, payload: body.payload };
}
